import {
  Component,
  ElementRef,
  HostListener,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { formatNumber } from "@angular/common";
import { prefersReducedMotion } from "@bitwarden/web-vault/app/shared/utils/reduced-motion.util";
import "./odometer-number.component.scss";

// "N digits -> N seconds" for the whole visible spin (flourish + carry landing), uncapped.
// Excludes INITIAL_MOUNT_DELAY_MS and SETTLE_BUFFER_MS.
const PHASE1_BASE_MS = 1000;
const PHASE1_PER_DIGIT_MS = 1000;
// Ceiling for magnitudeTotalMs's "one order of magnitude, one extra second" scale — past this many
// orders (magnitude >= 10 ** this), the total budget stops growing and just holds at
// PHASE1_BASE_MS + MAGNITUDE_TOTAL_MS_MAX_ORDERS * PHASE1_PER_DIGIT_MS (5s at the current values).
const MAGNITUDE_TOTAL_MS_MAX_ORDERS = 4;

// Units' loop-equivalents when NOT magnitude-seeded, scaled by the group's own duration so a short
// tick doesn't cram the same loop count into a tiny time budget and race through it.
const UNITS_MS_PER_LOOP = 250;
const UNITS_VISUAL_REVS_MIN = 3;
const UNITS_VISUAL_REVS_MAX = 12;

function unitsVisualRevsFor(targetMs: number): number {
  return Math.min(
    UNITS_VISUAL_REVS_MAX,
    Math.max(UNITS_VISUAL_REVS_MIN, Math.round(targetMs / UNITS_MS_PER_LOOP)),
  );
}

// Integer increases seed loop count from the real size of the jump (a real odometer clicks further
// for "+20" than "+10"), not the time budget above. Piecewise: linear below
// FORWARD_MAGNITUDE_CURVE_START_DIFF (+10 -> 1 loop), an ease-out (sqrt) curve up to
// FORWARD_MAGNITUDE_CURVE_END_DIFF, then flat at FORWARD_MAGNITUDE_LOOPS_MAX. The two boundaries
// line up exactly (2 loops at 20, 8 at 1,000) so there's no seam. Shared by every place, not just
// units — magnitudeLoopsFor is always called with the jump scaled to that digit's own resolution
// (see placeOf/magnitudeVisualRevsFor), so lowering this affects tens/hundreds/etc. the same way.
const FORWARD_MAGNITUDE_CURVE_START_DIFF = 20;
const FORWARD_MAGNITUDE_CURVE_END_DIFF = 1000;
const FORWARD_MAGNITUDE_CURVE_START_LOOPS = 2;
export const FORWARD_MAGNITUDE_LOOPS_MAX = 8;

function magnitudeLoopsFor(magnitude: number): number {
  const diff = Math.abs(magnitude);
  if (diff <= FORWARD_MAGNITUDE_CURVE_START_DIFF) {
    return Math.floor(diff / 10);
  }
  if (diff >= FORWARD_MAGNITUDE_CURVE_END_DIFF) {
    return FORWARD_MAGNITUDE_LOOPS_MAX;
  }
  const t =
    (diff - FORWARD_MAGNITUDE_CURVE_START_DIFF) /
    (FORWARD_MAGNITUDE_CURVE_END_DIFF - FORWARD_MAGNITUDE_CURVE_START_DIFF);
  const eased = Math.sqrt(t);
  return Math.round(
    FORWARD_MAGNITUDE_CURVE_START_LOOPS +
      (FORWARD_MAGNITUDE_LOOPS_MAX - FORWARD_MAGNITUDE_CURVE_START_LOOPS) * eased,
  );
}

function magnitudeVisualRevsFor(magnitude: number): number {
  return magnitudeLoopsFor(magnitude) + 1;
}

// Place value from an integer column's key ("i1" -> 1) — scales a magnitude to that digit's own
// resolution (tens ÷10, hundreds ÷100, ...) before feeding it to magnitudeLoopsFor.
function placeOf(key: string): number {
  return Number(key.slice(1));
}

// Pace slowdown per digit moving left, paired with computeSpinTiming's rev-count shrink.
const CHAIN_SPEED_GROWTH = 1.3;
// CHAIN_SPEED_GROWTH ** j compounds exponentially with chain position, and each digit's own delay
// is seeded from that (still-growing) pace rather than from its own (floor-clamped) rendered
// duration — the two diverge fast enough that by chain position 5 a digit's delay can already land
// past its right neighbour's finish, blowing the "N digits -> N seconds" budget (phase1TotalMs) and
// breaking the finish-before-neighbour invariant entirely (verified by simulation: uncapped, a
// 6-digit chain already overshoots, and a 15-digit one balloons to ~86s against an intended 15s).
// Freezing both the pace and the delay increment at this index once reached keeps every chain up to
// 5 digits byte-for-byte identical to the uncapped formula (this is exactly where it was already
// about to diverge) while bounding every longer chain to land right on phase1TotalMs.
// TODO: raise or remove this once computeSpinTiming's pacing curve is reworked from the ground up
// to hold its own timing contract at any chain length (see PR discussion).
const MAX_PACED_CHAIN_INDEX = 4;

// Floor on any digit's motion so the --ease-in-out curve reads as a decelerating glide, not a snap.
export const MIN_DIGIT_DURATION_MS = 1300;
// How far behind its neighbour's finish a digit lands — keeps MIN_DIGIT_DURATION_MS's floor from
// letting a late-starting digit catch up.
export const FINISH_GAP_MS = 80;
// Page-entry only: a short pause before the reveal starts so it doesn't fire before the card renders.
export const INITIAL_MOUNT_DELAY_MS = 300;
// Share of the chain's own budget (phase1TotalMs) reserved for the final synchronized carry/borrow
// landing; the remainder is phase 1's flourish. A fraction (not a flat add-on) so the two always sum
// back to exactly phase1TotalMs.
export const CARRY_LANDING_FRACTION = 0.4;
// Slack after the last finish time before settle() snaps every column to rest — too tight and
// settle's reset can fire before the browser paints the transition's own tail end.
const SETTLE_BUFFER_MS = 180;
const CROSSFADE_DURATION_MS = 200;
// A column dropped from the final value spins down to 0 alongside everything else instead of
// vanishing instantly (see backwardExitingIntColumns), and only starts its own width-collapse once
// that spin has settled. Collapses aren't simultaneous — each one is staggered so the digit closest
// to the surviving ones (i.e. the one being "pushed" out from the right) collapses first, then the
// next one out, and so on — one real odometer wheel shoving the next out of the way, not every
// dropped wheel vanishing in place at once.
//
// The stagger (EXIT_COLLAPSE_STAGGER_MS) is deliberately shorter than each digit's own collapse
// (EXIT_COLLAPSE_DURATION_MS), so a later digit starts collapsing while the one before it is still
// mid-motion — with linear easing (see the .scss) that overlap is what actually reads as one
// continuous "wipe" instead of N separate collapses that each visibly decelerate to a dead stop
// before the next one snaps to full speed. A dropped chain of N digits still takes longer the more
// of them there are — (N-1) * EXIT_COLLAPSE_STAGGER_MS + EXIT_COLLAPSE_DURATION_MS end to end — just
// without a hitch at every step.
const EXIT_COLLAPSE_STAGGER_MS = 60;
const EXIT_COLLAPSE_DURATION_MS = 180;

// Row that counts as digit "0". Sized so even the largest possible single backward move
// ((FORWARD_MAGNITUDE_LOOPS_MAX + 1) * 10 - 1 rows) can subtract from a just-settled row without
// going negative — baselineRow can't grow its way out of this later, since any row already tracked
// in rowByKey was computed relative to whatever baseline was current when it settled, and there's no
// way to re-base it without desyncing from what's already painted on screen (see applyDiff).
export const BASELINE_ROW = (FORWARD_MAGNITUDE_LOOPS_MAX + 1) * 10;
// Default strip length: BASELINE_ROW rows plus headroom above it. Grows per-diff (see
// stripRowsFor) and shrinks back to this once a spin settles (see settle()).
export const DIGIT_STRIP_ROWS = BASELINE_ROW + 20;
// A fixed px value (not em) so translateY(-row * ROW_HEIGHT_PX) always lands on a whole device
// pixel — an em value (e.g. 1.1em @ 36px = 39.6px) carries a different fractional-pixel remainder
// depending on row's value, which the browser then snaps independently per column, so two columns
// only a few rows apart can render a hair off from each other even when both show the right digit.
const ROW_HEIGHT_PX = 40;

export interface OdometerColumn {
  key: string;
  char: string;
  kind: "digit" | "separator";
  /**
   * Forward: full loops this column spins before landing, counted from the leftmost digit that
   * changed (the pivot gets 0; each digit right of it gets one more than its left neighbour, even
   * if its own value is unchanged — a real odometer's lower wheels spin through a carry too).
   *
   * Backward integer columns: floor(rowSteps / 10), capped — driven by real magnitude, not position.
   *
   * Null when nothing right of it (inclusive) animates, or it's a separator.
   */
  loops: number | null;
  /**
   * Backward integer columns only: rows to travel backward from this column's own current row (see
   * backwardIntColumns) — the real click count, with the loop portion eased through the shared
   * magnitudeLoopsFor curve (only whole loops are ever adjusted, never the remainder, so the landing
   * digit stays exact). Null for anything else — forward and fraction columns use loops instead.
   */
  rowSteps: number | null;
  /**
   * Which way this column travels — shared by every column in its group, from comparing the whole
   * group's value (a borrow like 5,000 -> 4,999 flips each digit's own face upward even though the
   * group decreased). Meaningless when `loops` is null.
   */
  direction: 1 | -1;
  entering: boolean;
  exiting: boolean;
}

interface RenderColumn {
  key: string;
  kind: "digit" | "separator";
  char: string;
  index: number; // left-to-right ordinal among digit columns only
  row: number;
  translate: string;
  disableTransition: boolean;
  exiting: boolean;
  delayMs: number;
  durationMs: number;
  // Width-collapse-only timing (see settle()'s staggerExitTimings) — separate from delayMs/
  // durationMs above, which drive the strip's own transform transition on a different element.
  // Undefined outside the collapse itself; the template treats that the same as 0.
  exitDelayMs?: number;
  exitDurationMs?: number;
}

function extractDigits(segment: string): string[] {
  return segment.split("").filter((ch) => ch >= "0" && ch <= "9");
}

interface IntFracDigits {
  intDigits: string[];
  fracDigits: string[];
}

function splitIntFrac(formatted: string, decimalSeparator: string): IntFracDigits {
  const dot = formatted.indexOf(decimalSeparator);
  return {
    intDigits: extractDigits(dot === -1 ? formatted : formatted.slice(0, dot)),
    fracDigits: extractDigits(dot === -1 ? "" : formatted.slice(dot + 1)),
  };
}

interface DigitSlot {
  key: string;
  char: string;
  /** true if this digit's value differs from before, or it didn't exist before (entering). */
  changed: boolean;
  entering: boolean;
}

// Leftmost changed/entering digit is the "pivot"; every digit from there rightward gets a loop
// count relative to it, even if its own value is unchanged. Nothing left of the pivot animates.
function assignLoops(slots: DigitSlot[], direction: 1 | -1): OdometerColumn[] {
  const pivot = slots.findIndex((slot) => slot.changed);
  return slots.map((slot, i) => ({
    key: slot.key,
    char: slot.char,
    kind: "digit",
    loops: pivot === -1 || i < pivot ? null : i - pivot,
    rowSteps: null,
    direction,
    entering: slot.entering,
    exiting: false,
  }));
}

// Backward counterpart of assignLoops, computed from real arithmetic instead of cascade position:
// rawSteps is how many times a digit's wheel would click back if the integer part were decremented
// one at a time — floor(prevValue / 10^place) - floor(nextValue / 10^place). A higher place ticking
// down by N forces every place below it through N full reverse revolutions, matching a real
// odometer's linked wheels. The loop portion is eased through magnitudeLoopsFor — the same curve
// forward increases use — rather than rendered literally, or an extreme swing would need a digit
// strip with tens of thousands of DOM rows; only whole loops are ever capped, never the remainder,
// so the landing digit is unaffected. Number() is safe here for any realistic balance.
function backwardIntColumns(prevInt: string[], nextInt: string[]): OdometerColumn[] {
  const prevValue = prevInt.length ? Number(prevInt.join("")) : 0;
  const nextValue = nextInt.length ? Number(nextInt.join("")) : 0;
  return nextInt.map((char, i) => {
    const placeFromRight = nextInt.length - 1 - i;
    const scale = 10 ** placeFromRight;
    const rawSteps = Math.floor(prevValue / scale) - Math.floor(nextValue / scale);
    const cappedLoops = rawSteps === 0 ? null : magnitudeLoopsFor(rawSteps);
    return {
      key: `i${placeFromRight}`,
      char,
      kind: "digit",
      loops: cappedLoops,
      rowSteps: cappedLoops === null ? null : (rawSteps % 10) + cappedLoops * 10,
      direction: -1,
      entering: false,
      exiting: false,
    };
  });
}

// Leading places dropped entirely by a shrinking integer part (e.g. 109 -> 9 drops hundreds/tens) —
// only ever reached when nextInt is shorter than prevInt, which compareIntDigits always judges as
// backward regardless of digit values, so these always land on "0" the same way backwardIntColumns'
// own places do (nextValue is necessarily smaller than 10**placeFromRight here, so its floor at that
// scale is always 0). Given a real rowSteps/loops instead of sitting inert, these spin down like any
// other backward column instead of vanishing the instant the diff is computed — the caller marks
// them `exiting` so they're only actually dropped from the DOM once that spin-down has settled.
function backwardExitingIntColumns(prevInt: string[], nextInt: string[]): OdometerColumn[] {
  const prevValue = prevInt.length ? Number(prevInt.join("")) : 0;
  const nextValue = nextInt.length ? Number(nextInt.join("")) : 0;
  const columns: OdometerColumn[] = [];
  for (
    let placeFromRight = prevInt.length - 1;
    placeFromRight >= nextInt.length;
    placeFromRight--
  ) {
    const scale = 10 ** placeFromRight;
    const rawSteps = Math.floor(prevValue / scale) - Math.floor(nextValue / scale);
    const cappedLoops = rawSteps === 0 ? null : magnitudeLoopsFor(rawSteps);
    columns.push({
      key: `i${placeFromRight}`,
      char: "0",
      kind: "digit",
      loops: cappedLoops,
      rowSteps: cappedLoops === null ? null : (rawSteps % 10) + cappedLoops * 10,
      direction: -1,
      entering: false,
      exiting: true,
    });
  }
  return columns;
}

// Whole-group comparison, not per-digit — a borrow like 5,000 -> 4,999 flips every digit's own face
// upward even though the group decreased.
function compareIntDigits(prevInt: string[], nextInt: string[]): -1 | 0 | 1 {
  if (nextInt.length !== prevInt.length) {
    return nextInt.length > prevInt.length ? 1 : -1;
  }
  for (let i = 0; i < nextInt.length; i++) {
    if (nextInt[i] !== prevInt[i]) {
      return nextInt[i] > prevInt[i] ? 1 : -1;
    }
  }
  return 0;
}

// Same idea, but padded on the right — fraction place value shrinks left-to-right, so a shorter
// array pads with trailing "0"s (0.5 vs 0.45 compares as "50" vs "45").
function compareFracDigits(prevFrac: string[], nextFrac: string[]): -1 | 0 | 1 {
  const len = Math.max(prevFrac.length, nextFrac.length);
  for (let i = 0; i < len; i++) {
    const prevDigit = prevFrac[i] ?? "0";
    const nextDigit = nextFrac[i] ?? "0";
    if (prevDigit !== nextDigit) {
      return nextDigit > prevDigit ? 1 : -1;
    }
  }
  return 0;
}

function intDigitSlots(nextInt: string[], prevInt: string[]): DigitSlot[] {
  return nextInt.map((char, i) => {
    const placeFromRight = nextInt.length - 1 - i;
    const hasPrev = placeFromRight < prevInt.length;
    const prevChar = hasPrev ? prevInt[prevInt.length - 1 - placeFromRight] : null;
    return {
      key: `i${placeFromRight}`,
      char,
      changed: prevChar === null || prevChar !== char,
      entering: prevChar === null,
    };
  });
}

function fracDigitSlots(nextFrac: string[], prevFrac: string[]): DigitSlot[] {
  return nextFrac.map((char, i) => {
    const prevChar = i < prevFrac.length ? prevFrac[i] : null;
    return {
      key: `f${i}`,
      char,
      changed: prevChar === null || prevChar !== char,
      entering: prevChar === null,
    };
  });
}

/**
 * Diffs two already-formatted number strings into a left-to-right column list (see
 * OdometerColumn). Integer digits align from the right (place value from the ones digit); fraction
 * digits align from the left of the decimal point, since the fractional digit count can itself
 * change. Pivot/cascade is found independently per group. An empty `prevFormatted` makes every
 * digit `entering`.
 */
export function diffOdometerColumns(
  prevFormatted: string,
  nextFormatted: string,
  decimalSeparator = ".",
): OdometerColumn[] {
  const { intDigits: prevInt, fracDigits: prevFrac } = splitIntFrac(
    prevFormatted,
    decimalSeparator,
  );
  const { intDigits: nextInt, fracDigits: nextFrac } = splitIntFrac(
    nextFormatted,
    decimalSeparator,
  );

  const intDirection = compareIntDigits(prevInt, nextInt) === -1 ? -1 : 1;
  const fracDirection = compareFracDigits(prevFrac, nextFrac) === -1 ? -1 : 1;
  const intColumns =
    intDirection === -1
      ? backwardIntColumns(prevInt, nextInt)
      : assignLoops(intDigitSlots(nextInt, prevInt), intDirection);
  const fracColumns = assignLoops(fracDigitSlots(nextFrac, prevFrac), fracDirection);

  const columns: OdometerColumn[] = [];
  let seenDot = false;
  let intCursor = 0;
  let fracCursor = 0;

  for (const ch of nextFormatted) {
    if (ch === decimalSeparator) {
      seenDot = true;
      columns.push({
        key: `sep-${columns.length}`,
        char: ch,
        kind: "separator",
        loops: null,
        rowSteps: null,
        direction: 1,
        entering: false,
        exiting: false,
      });
      continue;
    }
    if (ch < "0" || ch > "9") {
      columns.push({
        key: `sep-${columns.length}`,
        char: ch,
        kind: "separator",
        loops: null,
        rowSteps: null,
        direction: 1,
        entering: false,
        exiting: false,
      });
      continue;
    }

    if (!seenDot) {
      columns.push(intColumns[intCursor]);
      intCursor += 1;
    } else {
      columns.push(fracColumns[fracCursor]);
      fracCursor += 1;
    }
  }

  // Only ever non-empty when nextInt is shorter than prevInt — compareIntDigits always judges a
  // length mismatch as backward regardless of digit values, so intColumns above is already
  // backwardIntColumns' output whenever this loop has anything to do.
  const exitingInt = backwardExitingIntColumns(prevInt, nextInt);
  // Dropped fraction digits (e.g. 10.25 -> 10) are NOT re-added here the way exitingInt is above:
  // this loop only walks nextFormatted's own characters, so a shrinking fraction has no decimal
  // point/separator column to anchor them to — they'd render appended after the last integer
  // digit with nothing between, reading as "1025" until settle() drops them. loops/rowSteps are
  // null either way (they never spin), so there's no animation lost by leaving them out.

  return [...exitingInt, ...columns];
}

// `digits - 1`, same length (e.g. ["1","0","0","0"] -> ["0","9","9","9"]) — the resting point while
// the final carry is pending. Null when already all zeros (nothing to carry from).
export function decrementDigits(digits: string[]): string[] | null {
  const result = [...digits];
  for (let i = result.length - 1; i >= 0; i--) {
    if (result[i] !== "0") {
      result[i] = String(Number(result[i]) - 1);
      return result;
    }
    result[i] = "9";
  }
  return null;
}

// Mirror of decrementDigits for borrows: `digits + 1`, same length (e.g. ["5","2","0","9"] ->
// ["5","2","1","0"]). Null when already all 9s — growing the array by a digit doesn't fit this
// representation, so the caller skips the borrow phase (backwardIntColumns already handles that
// case correctly on its own, e.g. 1,000 -> 999).
export function incrementDigits(digits: string[]): string[] | null {
  const result = [...digits];
  for (let i = result.length - 1; i >= 0; i--) {
    if (result[i] !== "9") {
      result[i] = String(Number(result[i]) + 1);
      return result;
    }
    result[i] = "0";
  }
  return null;
}

// Substitutes `formatted`'s integer-part digits with `replacement`, leaving separators and fraction
// digits untouched — builds a synthetic phase-1 target without re-deriving locale grouping by hand.
function withReplacedIntegerDigits(
  formatted: string,
  decimalSeparator: string,
  replacement: string[],
): string {
  let result = "";
  let seenDot = false;
  let cursor = 0;
  for (const ch of formatted) {
    if (ch === decimalSeparator) {
      seenDot = true;
      result += ch;
      continue;
    }
    if (!seenDot && ch >= "0" && ch <= "9") {
      result += replacement[cursor];
      cursor += 1;
      continue;
    }
    result += ch;
  }
  return result;
}

function integerIncreased(prevInt: string[], nextInt: string[]): boolean {
  return compareIntDigits(prevInt, nextInt) === 1;
}

export function translateFor(row: number): string {
  return `translateY(-${row * ROW_HEIGHT_PX}px)`;
}

// Rows above baselineRow a diff needs for its loop count. applyDiff only ever grows the strip to
// this mid-spin, since an in-flight spin may still reference a high index; settle() is what shrinks
// it back once every row is at rest again.
function stripRowsFor(baselineRow: number, maxLoops: number): number {
  return baselineRow + (maxLoops + 1) * 10;
}

// Highest a single spin can target above baselineRow when it starts from rest (a settled row is at
// most baselineRow + 9): up to 9 rows to the landing digit, FORWARD_MAGNITUDE_LOOPS_MAX magnitude
// loops and a carry's +1, rounded up to whole loops. A diff that interrupts an in-flight spin
// continues from that spin's row, so a burst can climb well past this; applyDiff rebases any column
// that would (see rebaseDriftedColumns), which is what keeps the strip bounded during a burst.
export const MAX_ROWS_ABOVE_BASELINE = (FORWARD_MAGNITUDE_LOOPS_MAX + 3) * 10;

function buildDigitStrip(rows: number): number[] {
  return Array.from({ length: rows }, (_, i) => i % 10);
}

function getDecimalSeparator(locale: string): string {
  const part = new Intl.NumberFormat(locale).formatToParts(1.1).find((p) => p.type === "decimal");
  return part?.value ?? ".";
}

interface SpinTiming {
  perKey: Map<
    string,
    {
      delayMs: number;
      durationMs: number;
      /** Always >= the column's semantic loops; only the rendered distance differs, never the
       *  landing digit. */
      visualLoops: number;
    }
  >;
  /** When every spinning column finishes — safe to start the carry landing or final settle. */
  totalMs: number;
  /** Duration of applyCarrySync's landing (see CARRY_LANDING_FRACTION). 0 if there's no carry. */
  carryLandingMs: number;
}

export function phase1TotalMs(chainLength: number): number {
  return PHASE1_BASE_MS + (chainLength - 1) * PHASE1_PER_DIGIT_MS;
}

// Magnitude-seeded counterpart to phase1TotalMs — the budget for a magnitude-seeded integer chain
// (see computeSpinTiming) depends only on how big the actual jump is, not on how many digits a
// carry/borrow happens to ripple through: one order of magnitude (a power of 10) per extra second,
// so +1 is 1s, +50 is 2s, +500 is 3s, +5,000 is 4s, +50,000 is 5s — capped there
// (MAGNITUDE_TOTAL_MS_MAX_ORDERS) rather than growing forever. A 999 -> 1,000 tick (magnitude 1) is
// quick even though the carry ripples through all four digits; a 999 -> 1,999 jump (magnitude
// 1,000) takes 4s, same as any other 4-digit-magnitude jump would. Independent of magnitudeLoopsFor
// (the visual loop count) — this only decides how long the whole thing takes, not how many times
// units spins along the way.
export function magnitudeTotalMs(magnitude: number): number {
  const diff = Math.abs(magnitude);
  const ordersOfMagnitude = diff < 1 ? 0 : Math.floor(Math.log10(diff));
  const cappedOrders = Math.min(MAGNITUDE_TOTAL_MS_MAX_ORDERS, Math.max(0, ordersOfMagnitude));
  return PHASE1_BASE_MS + cappedOrders * PHASE1_PER_DIGIT_MS;
}

/**
 * Assigns each spinning column a start delay, duration, and loop count, per group (integer/fraction):
 *
 * - Units starts immediately, via unitsVisualRevsFor(targetMs) — or, for an integer increase with
 *   `intMagnitude`, magnitudeVisualRevsFor(intMagnitude) instead, seeding from the jump's real size.
 * - Each digit further left starts once its right neighbour finishes its own first loop, at that
 *   neighbour's pace (CHAIN_SPEED_GROWTH times slower) and a rev budget interpolated down to
 *   exactly 1 (visualLoops 0) at the pivot.
 * - MIN_DIGIT_DURATION_MS floors a short move so it still visibly decelerates; FINISH_GAP_MS then
 *   re-clamps finish times so that floor can't let a late-starting digit catch up.
 * - The pivot (chainLength >= 2) is stretched to finish exactly FINISH_GAP_MS before its neighbour,
 *   instead of stopping early on its own short natural duration.
 *
 * `carrySyncKeys` tells this whether to reserve a CARRY_LANDING_FRACTION share of the integer
 * group's budget for applyCarrySync's landing, so flourish + landing sum to exactly phase1TotalMs.
 */
export function computeSpinTiming(
  diff: OdometerColumn[],
  initialDelayMs: number,
  carrySyncKeys: Set<string>,
  intMagnitude?: number,
): SpinTiming {
  const perKey = new Map<string, { delayMs: number; durationMs: number; visualLoops: number }>();
  let totalMs = 0;
  let carryLandingMs = 0;

  const groups = [
    diff.filter((c) => c.kind === "digit" && c.key.startsWith("i")),
    diff.filter((c) => c.kind === "digit" && c.key.startsWith("f")),
  ];

  for (const group of groups) {
    const spinning = group.filter((c) => c.loops !== null);
    if (!spinning.length) {
      continue;
    }

    const chainLength = spinning.length;
    const isIntegerGroup = group[0].key.startsWith("i");
    // Magnitude-seeded: the budget depends only on how big the jump actually is (magnitudeTotalMs),
    // not on how many digits a carry/borrow happens to ripple through — a 999 -> 1,000 tick is
    // magnitude 1 and stays quick even though it ripples through all four digits. Otherwise (no
    // intMagnitude, e.g. fraction digits), budget off the wider of chainLength/carrySyncKeys.size —
    // a carry can ripple through more digits than actually spin in phase 1 (e.g. 5,291 -> 5,300
    // only spins units).
    const chainTotalMs =
      isIntegerGroup && intMagnitude !== undefined
        ? magnitudeTotalMs(intMagnitude)
        : phase1TotalMs(isIntegerGroup ? Math.max(chainLength, carrySyncKeys.size) : chainLength);
    // Only the integer group ever carries.
    const reservedForCarryMs =
      isIntegerGroup && carrySyncKeys.size > 0 ? chainTotalMs * CARRY_LANDING_FRACTION : 0;
    if (reservedForCarryMs > 0) {
      carryLandingMs = reservedForCarryMs;
    }
    const targetMs = chainTotalMs - reservedForCarryMs;

    // Loop-equivalents per position (0 = units), interpolated down to exactly 1 (visualLoops 0) at
    // the pivot. Magnitude-seeded skips both the time-based formula and the "chainLength 1 -> no
    // flourish" special case: a lone spinning digit (e.g. 5,200 -> 5,210) still gets a full loop for
    // a +10 jump.
    const totalRevs: number[] = [
      isIntegerGroup && intMagnitude !== undefined
        ? magnitudeVisualRevsFor(intMagnitude)
        : chainLength === 1
          ? 1
          : Math.max(unitsVisualRevsFor(targetMs), chainLength),
    ];
    for (let j = 1; j < chainLength; j++) {
      const col = spinning[spinning.length - 1 - j];
      // Magnitude-seeded: each digit gets its own independent revs, scaled to its own place value
      // (tens ÷10, hundreds ÷100), not interpolated from units.
      if (isIntegerGroup && intMagnitude !== undefined) {
        totalRevs.push(magnitudeVisualRevsFor(intMagnitude / 10 ** placeOf(col.key)));
        continue;
      }
      const semanticMin = (col.loops ?? 0) + 1;
      const interpolated = Math.round(totalRevs[0] - (j * (totalRevs[0] - 1)) / (chainLength - 1));
      totalRevs.push(Math.max(semanticMin, interpolated));
    }

    const baseLoopMs = targetMs / totalRevs[0];
    let delayMs = initialDelayMs;
    // Caps this digit's finish strictly before its right neighbour's.
    let maxFinishMs = Infinity;
    const digitFloorMs = Math.min(MIN_DIGIT_DURATION_MS, targetMs);
    for (let j = 0; j < chainLength; j++) {
      const col = spinning[spinning.length - 1 - j];
      const perLoopMs = baseLoopMs * CHAIN_SPEED_GROWTH ** Math.min(j, MAX_PACED_CHAIN_INDEX);
      const naturalDurationMs = Math.max(totalRevs[j] * perLoopMs, digitFloorMs);
      // The pivot is stretched to land exactly on its ceiling instead of finishing early.
      const isPivot = j === chainLength - 1 && Number.isFinite(maxFinishMs);
      const finishMs = isPivot ? maxFinishMs : Math.min(delayMs + naturalDurationMs, maxFinishMs);
      const durationMs = Math.max(finishMs - delayMs, MIN_DIGIT_DURATION_MS / 6);
      perKey.set(col.key, { delayMs, durationMs, visualLoops: totalRevs[j] - 1 });
      totalMs = Math.max(totalMs, delayMs + durationMs);
      maxFinishMs = delayMs + durationMs - FINISH_GAP_MS;
      // Next (further-left) digit waits for this one's own first loop — except in a magnitude-seeded
      // *forward* (carry) integer chain (9 -> 10, 9 -> 19, 9 -> 49, 99 -> 109, 99 -> 129, ...). A
      // carry's crossing point (unit wrapping 9 -> 0) is the very first step of its own motion — it
      // starts at 9 and rolls over immediately — so the digit above it has nothing worth waiting
      // for; every digit's own revs are already scaled to its own place value (totalRevs above)
      // instead of interpolated from chain position, so they can all just start together and let
      // the pivot's own duration-stretch (isPivot, below) land it at the right moment.
      //
      // A *backward* (borrow) chain is the mirror image: the crossing point (unit wrapping 0 -> 9)
      // is the very LAST step of its own loop, not the first — so the digit above it genuinely has
      // nothing to move *for* until units gets there, and syncing them anyway (as if it were a
      // carry) makes the digit above sit fully settled while units is still visibly spinning through
      // however many loops its own magnitude gives it — the more loops (the bigger the borrow), the
      // longer that dead wait looks. So backward keeps the original wait, same as a non-magnitude
      // chain, except when this digit itself has no real loop to wait for (totalRevs[j] <= 1, e.g.
      // a plain -1 like 10 -> 9, 100 -> 99) — there the "wait" would just be waiting on a step that
      // isn't really happening, so it's skipped the same way a carry's is.
      const isMagnitudeSeededForwardCarry =
        isIntegerGroup && intMagnitude !== undefined && col.direction === 1;
      const isTrivialBackwardStep =
        isIntegerGroup && intMagnitude !== undefined && col.direction === -1 && totalRevs[j] <= 1;
      delayMs +=
        j < MAX_PACED_CHAIN_INDEX && !isMagnitudeSeededForwardCarry && !isTrivialBackwardStep
          ? perLoopMs
          : 0;
    }
  }

  // A carry can ripple with nothing spinning in phase 1 (e.g. 4999 -> 5000) — reserve the whole
  // chain's budget for the landing. Magnitude-seeded when available, same as chainTotalMs above.
  if (carryLandingMs === 0 && carrySyncKeys.size > 0) {
    carryLandingMs =
      intMagnitude !== undefined
        ? magnitudeTotalMs(intMagnitude)
        : phase1TotalMs(carrySyncKeys.size);
  }

  return { perKey, totalMs, carryLandingMs };
}

/**
 * Extends (never shortens) every column in `carryKeys` to finish at the same instant — a shared
 * anchor starting from units' own phase-1 finish (or initialDelayMs if nothing spun) and landing
 * timing.carryLandingMs later — so the final "+1" plays as one continuous glide, not a separate
 * tick. A column that wasn't spinning in phase 1 gets a fresh entry instead of an extension.
 */
export function applyCarrySync(
  timing: SpinTiming,
  carryKeys: Set<string>,
  initialDelayMs: number,
): void {
  if (!carryKeys.size) {
    return;
  }

  const unitsInfo = timing.perKey.get("i0");
  const carryStartMs = unitsInfo ? unitsInfo.delayMs + unitsInfo.durationMs : initialDelayMs;
  const anchorMs = carryStartMs + timing.carryLandingMs;

  if (unitsInfo) {
    unitsInfo.durationMs = anchorMs - unitsInfo.delayMs;
  } else {
    timing.perKey.set("i0", {
      delayMs: initialDelayMs,
      durationMs: anchorMs - initialDelayMs,
      visualLoops: 0,
    });
  }

  for (const key of carryKeys) {
    if (key === "i0") {
      continue; // already the anchor itself — nothing to sync it to.
    }
    const existing = timing.perKey.get(key);
    if (existing) {
      existing.durationMs = Math.max(existing.durationMs, anchorMs - existing.delayMs);
    } else {
      timing.perKey.set(key, {
        delayMs: initialDelayMs,
        durationMs: anchorMs - initialDelayMs,
        visualLoops: 0,
      });
    }
  }

  timing.totalMs = Math.max(timing.totalMs, anchorMs);
}

/**
 * Widens `carryKeys` to every digit still spinning in the integer cascade, so applyCarrySync's
 * finish-time sync covers the whole chain, not just the digits the "+1" itself flips — a real
 * odometer's wheels are geared together, so an unwidened in-between digit (e.g. hundreds in 0 ->
 * 5210, where only tens/units flip) would finish early, visibly disconnected from the rest.
 * carryBonus (see applyDiff) still keys off the narrower, un-widened `carryKeys`.
 */
export function expandCarrySyncKeys(diff: OdometerColumn[], carryKeys: Set<string>): Set<string> {
  const syncKeys = new Set(carryKeys);
  if (carryKeys.size) {
    for (const col of diff) {
      if (col.kind === "digit" && col.key.startsWith("i") && col.loops !== null) {
        syncKeys.add(col.key);
      }
    }
  }
  return syncKeys;
}

@Component({
  selector: "app-odometer-number",
  templateUrl: "./odometer-number.component.html",
})
export class OdometerNumberComponent implements OnDestroy {
  value = input.required<number>();
  committed = output<void>();

  protected digitStrip = buildDigitStrip(DIGIT_STRIP_ROWS);
  protected readonly ariaLabel = signal("");
  protected readonly reducedMotion = signal(false);
  protected readonly crossFadePulse = signal(false);
  protected readonly renderColumns = signal<RenderColumn[]>([]);

  private readonly locale = inject(LOCALE_ID);
  private readonly ngZone = inject(NgZone);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly decimalSeparator = getDecimalSeparator(this.locale);

  private previousFormatted = "";
  private readonly rowByKey = new Map<string, number>();
  private stripRows = DIGIT_STRIP_ROWS;
  // The array index that represents digit "0" — see BASELINE_ROW.
  private baselineRow = BASELINE_ROW;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private enterRafId: number | null = null;
  // Set while enterRafId is pending, so an interrupting value can resolve it synchronously instead
  // of abandoning those columns at their start row.
  private pendingSpinTargets: Map<string, number> | null = null;
  private crossFadeTimer: ReturnType<typeof setTimeout> | null = null;
  private settleRafId: number | null = null;
  // Set while a dropped column's post-settle width-collapse is playing (see EXIT_COLLAPSE_STAGGER_MS/
  // EXIT_COLLAPSE_DURATION_MS) —
  // cancelled (not resolved) by a new diff, since that diff's own fresh columns/exitingKeys fully
  // supersede whatever this one was about to remove.
  private exitCleanupTimer: ReturnType<typeof setTimeout> | null = null;
  // What scheduleSettle's setTimeout would pass to settle(), kept outside that closure so
  // onWindowResize can force the same completion early.
  private pendingCompletion: { exitingKeys: string[] } = { exitingKeys: [] };

  constructor() {
    effect(
      () => {
        const next = formatNumber(this.value(), this.locale, "1.0-3");
        this.ariaLabel.set(next);
        this.applyDiff(next);
        this.previousFormatted = next;
      },
      { allowSignalWrites: true },
    );
  }

  // Browser zoom retargets an in-progress transition's computed px value, which our settle() timer
  // has no way to know about — forcing the spin to finish immediately guarantees the digit shown
  // is never stale. Guarded to actual mid-transition columns so it doesn't also cut short the
  // reduced-motion crossfade (a plain opacity fade unaffected by em/px retargeting).
  @HostListener("window:resize")
  protected onWindowResize(): void {
    if (!this.settleTimer) {
      return;
    }
    const isActivelyTransitioning = this.renderColumns().some(
      (c) => c.kind === "digit" && !c.disableTransition && c.durationMs > 0,
    );
    if (!isActivelyTransitioning) {
      return;
    }
    clearTimeout(this.settleTimer);
    this.settleTimer = null;
    this.finishSpin();
  }

  ngOnDestroy(): void {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
    }
    if (this.enterRafId !== null) {
      cancelAnimationFrame(this.enterRafId);
    }
    if (this.crossFadeTimer) {
      clearTimeout(this.crossFadeTimer);
    }
    if (this.settleRafId !== null) {
      cancelAnimationFrame(this.settleRafId);
    }
    if (this.exitCleanupTimer) {
      clearTimeout(this.exitCleanupTimer);
    }
  }

  private applyDiff(next: string): void {
    // A pending enter frame's columns are sitting at their start row waiting to be moved —
    // resolve it synchronously or they'd be stuck there, with rowByKey stale for this new diff.
    if (this.enterRafId !== null) {
      cancelAnimationFrame(this.enterRafId);
      this.enterRafId = null;
      if (this.pendingSpinTargets) {
        this.resolveSpinTargets(this.pendingSpinTargets);
        this.pendingSpinTargets = null;
      }
    }
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    if (this.settleRafId !== null) {
      cancelAnimationFrame(this.settleRafId);
      this.settleRafId = null;
      // settle()'s snap may not be applied yet; force it so this diff's moves start from it.
      // This needs the snap already in the DOM, which holds because Angular 18 runs this effect
      // after the change-detection tick that rendered it (#6164; re-check on an Angular upgrade,
      // the "(committed) handler that sets a new value" spec guards it).
      this.forceStyleFlush();
    }
    if (this.exitCleanupTimer) {
      // This diff's own fresh columns/exitingKeys (see below) fully supersede whatever the
      // previous one was about to drop — cancelled, not resolved: renderColumns.set() further
      // down already replaces any stale mid-collapse column wholesale.
      clearTimeout(this.exitCleanupTimer);
      this.exitCleanupTimer = null;
    }

    // Still used below to seed the reveal delay (INITIAL_MOUNT_DELAY_MS) and decide carry
    // eligibility for a first render — committed now fires on every finished spin, including this
    // one, so the card's glare replays right as the count-up lands rather than at raw mount.
    const isInitialMount = this.previousFormatted === "";
    const reduced = prefersReducedMotion();
    this.reducedMotion.set(reduced);

    if (reduced) {
      // The template never renders the per-digit columns while reduced motion is active (see the
      // .html's @if), but rowByKey/renderColumns must still track next's real digits — otherwise
      // turning reduced motion back off mid-session diffs the next real update against whatever was
      // last painted before it engaged, landing every unchanged column on a stale, wrong digit.
      this.snapToRest(next);
      this.pulseCrossFade();
      this.scheduleSettle(0, []);
      return;
    }

    // carryKeys/borrowKeys: which trailing digits structurally flip in the final "target ∓ 1 ->
    // target" step (see decrementDigits/incrementDigits) — e.g. 4999 -> 5000 carries all four
    // digits; 5,220 -> 5,209 borrows just tens/units. Only one set is ever populated.
    const { intDigits: prevInt } = isInitialMount
      ? { intDigits: [] as string[] }
      : splitIntFrac(this.previousFormatted, this.decimalSeparator);
    const { intDigits: nextInt } = splitIntFrac(next, this.decimalSeparator);
    const eligibleForCarry = isInitialMount
      ? nextInt.some((d) => d !== "0")
      : integerIncreased(prevInt, nextInt);
    const eligibleForBorrow = !isInitialMount && integerIncreased(nextInt, prevInt);

    let phase1Target = next;
    const carryKeys = new Set<string>();
    const borrowKeys = new Set<string>();
    if (eligibleForCarry) {
      const preInt = decrementDigits(nextInt);
      if (preInt) {
        preInt.forEach((d, i) => {
          if (d !== nextInt[i]) {
            carryKeys.add(`i${nextInt.length - 1 - i}`);
          }
        });
        if (carryKeys.size) {
          phase1Target = withReplacedIntegerDigits(next, this.decimalSeparator, preInt);
        }
      }
    } else if (eligibleForBorrow) {
      const postInt = incrementDigits(nextInt);
      if (postInt) {
        postInt.forEach((d, i) => {
          if (d !== nextInt[i]) {
            borrowKeys.add(`i${nextInt.length - 1 - i}`);
          }
        });
        if (borrowKeys.size) {
          phase1Target = withReplacedIntegerDigits(next, this.decimalSeparator, postInt);
        }
      }
    }

    const diff = diffOdometerColumns(this.previousFormatted, phase1Target, this.decimalSeparator);
    const flipKeys = carryKeys.size ? carryKeys : borrowKeys;
    const carrySyncKeys = expandCarrySyncKeys(diff, flipKeys);
    const intMagnitude = Math.abs(Number(nextInt.join("")) - Number(prevInt.join("")));

    const timing = computeSpinTiming(
      diff,
      isInitialMount ? INITIAL_MOUNT_DELAY_MS : 0,
      carrySyncKeys,
      intMagnitude,
    );
    applyCarrySync(timing, carrySyncKeys, isInitialMount ? INITIAL_MOUNT_DELAY_MS : 0);

    // Strip headroom: a high-water mark off the largest loop count, forward and backward tracked
    // separately since they grow the strip in opposite directions. Takes col.loops into account
    // alongside visualLoops, not instead of it — backward's col.loops comes from real borrow-ripple
    // arithmetic that computeSpinTiming's pacing has no way to know about, so visualLoops alone
    // could under-size the strip for a big backward swing.
    let maxForwardLoops = 0;
    let maxBackwardLoops = 0;
    for (const col of diff) {
      if (col.kind !== "digit" || col.loops === null) {
        continue;
      }
      const info = timing.perKey.get(col.key);
      if (!info) {
        continue;
      }
      const loops = Math.max(info.visualLoops, col.loops);
      if (col.direction === -1) {
        maxBackwardLoops = Math.max(maxBackwardLoops, loops);
      } else {
        maxForwardLoops = Math.max(maxForwardLoops, loops);
      }
    }
    for (const key of carryKeys) {
      const info = timing.perKey.get(key);
      if (info) {
        maxForwardLoops = Math.max(maxForwardLoops, info.visualLoops);
      }
    }

    // Defensive only (see BASELINE_ROW) — growing baselineRow here can't safely fix up rows already
    // tracked against the old, smaller baseline, so this should never actually need to trigger.
    if (maxBackwardLoops > 0) {
      this.baselineRow = Math.max(this.baselineRow, (maxBackwardLoops + 1) * 10);
    }

    // Each column's pending move to its real target row, applied after a forced style flush (see
    // resolveSpinTargets).
    const spinTargets = new Map<string, number>();
    let digitIndex = 0;
    // Where each column sat before this diff, for rebaseDriftedColumns (the map below overwrites
    // rowByKey with this diff's targets as it goes).
    const priorRowByKey = new Map(this.rowByKey);

    const mappedColumns: RenderColumn[] = diff.map((col) => {
      if (col.kind === "separator") {
        return {
          key: col.key,
          kind: "separator",
          char: col.char,
          index: -1,
          row: 0,
          translate: "",
          disableTransition: false,
          exiting: false,
          delayMs: 0,
          durationMs: 0,
        };
      }

      const index = digitIndex++;

      // col.exiting (dropped from the final value) is handled at settle() time, not here — an
      // int column in this state gets real backward loops/rowSteps (see
      // backwardExitingIntColumns) and spins down through the branches below same as any other
      // backward column; a frac column (still loops: null) falls through to the "unchanged"
      // bucket at the end and just sits at its current row. Either way it renders at full width
      // (RenderColumn.exiting stays false) until settle() actually drops it from the DOM — see
      // settle()'s own exiting handling for why the width-collapse waits that long.
      const carrying = carryKeys.has(col.key);
      const borrowing = borrowKeys.has(col.key);

      // Backward integer columns continue straight from wherever they're already sitting — a real
      // click-by-click decrement, not a fake glide from an invisible baseline. Never entering (a
      // backward group can't grow digits), so rowByKey always has a real prior row here. borrowing
      // subtracts one more row past rowSteps' plain landing spot (which only reaches phase1Target,
      // i.e. target + 1) — the mirror of carryBonus's "+1". A pure borrow rider (rowSteps === null)
      // still lands exactly one row back.
      if (col.direction === -1 && (col.rowSteps !== null || borrowing)) {
        const { delayMs, durationMs } = timing.perKey.get(col.key) ?? { delayMs: 0, durationMs: 0 };
        const startRow = this.rowByKey.get(col.key) ?? this.baselineRow;
        const rawTargetRow = startRow - (col.rowSteps ?? 0) - (borrowing ? 1 : 0);
        // Floor defensively at the real landing digit's own remainder (see BASELINE_ROW): a second
        // large backward jump landing before the first one settles can otherwise drive this
        // negative, scrolling the strip past its first rendered row. col.char is phase1Target's
        // digit — postInt (one past the real digit) when borrowing — so undo that offset first.
        const landingDigit = borrowing ? (Number(col.char) + 9) % 10 : Number(col.char);
        const targetRow = Math.max(rawTargetRow, landingDigit);
        this.rowByKey.set(col.key, targetRow);
        return {
          key: col.key,
          kind: "digit",
          char: col.char,
          index,
          row: targetRow,
          translate: translateFor(targetRow),
          disableTransition: false,
          exiting: false,
          delayMs,
          durationMs,
        };
      }

      const isForwardInt = col.key.startsWith("i") && col.direction === 1;

      // Every forward integer digit (pivot, carry rider, or cascading) continues from its own real
      // row and adds place-scaled magnitude loops on top (magnitudeLoopsFor, placeOf) — a +100 jump
      // gives tens one whole "hundred", the same way +10 gives units one "ten". col.char is
      // phase-1's own digit, one less than the true final answer when this column carries.
      // Excludes entering: a digit-count-growing jump's leading digit is a freshly-inserted DOM
      // node with no real prior row (see the reveal-then-jump dance further down).
      if (isForwardInt && !col.entering && (col.loops !== null || carrying || borrowing)) {
        const { delayMs, durationMs } = timing.perKey.get(col.key) ?? { delayMs: 0, durationMs: 0 };
        const currentRow = this.rowByKey.get(col.key) ?? this.baselineRow;
        const currentDigit = ((currentRow % 10) + 10) % 10;
        // col.char is phase1Target's digit: preInt (nextDigit - 1) when carrying, postInt
        // (nextDigit + 1) when borrowing — undo that offset to land on the real final digit.
        const finalDigit = carrying
          ? (Number(col.char) + 1) % 10
          : borrowing
            ? (Number(col.char) + 9) % 10
            : Number(col.char);
        const magnitudeLoops = magnitudeLoopsFor(intMagnitude / 10 ** placeOf(col.key));
        // A borrow's true motion is backward (a decrease) even though col.direction reads forward
        // here — the tie in diffOdometerColumns' compareIntDigits defaults to 1 whenever
        // phase1Target's int collapses back to previousFormatted's own digits (see incrementDigits).
        // Take the short hop back instead of the long way forward through 9.
        // Floor defensively at the real landing digit, same as the backward branch above (see its
        // comment on BASELINE_ROW) — a chain of interrupted borrows with no settle in between can
        // otherwise drive this below the strip's first rendered row.
        const targetRow = borrowing
          ? Math.max(
              currentRow - ((currentDigit - finalDigit + 10) % 10) - magnitudeLoops * 10,
              finalDigit,
            )
          : currentRow + ((finalDigit - currentDigit + 10) % 10) + magnitudeLoops * 10;
        this.rowByKey.set(col.key, targetRow);
        return {
          key: col.key,
          kind: "digit",
          char: col.char,
          index,
          row: targetRow,
          translate: translateFor(targetRow),
          disableTransition: false,
          exiting: false,
          delayMs,
          durationMs,
        };
      }

      // Fraction-only from here down (every forward integer digit was already caught above): same
      // direct-from-current-position idea for a fraction pivot, but no magnitude bonus.
      if (!col.entering && (col.loops === 0 || (col.loops === null && carrying))) {
        const { delayMs, durationMs } = timing.perKey.get(col.key) ?? { delayMs: 0, durationMs: 0 };
        const currentRow = this.rowByKey.get(col.key) ?? this.baselineRow;
        const currentDigit = ((currentRow % 10) + 10) % 10;
        const finalDigit = carrying ? (Number(col.char) + 1) % 10 : Number(col.char);
        // Direction-aware: a backward pivot (e.g. 3,450.60 -> 3,450.40) must hop the short way
        // back, not glide the long way forward through 9 like a forward pivot does.
        const targetRow =
          col.direction === -1
            ? currentRow - ((currentDigit - finalDigit + 10) % 10)
            : currentRow + ((finalDigit - currentDigit + 10) % 10);
        this.rowByKey.set(col.key, targetRow);
        return {
          key: col.key,
          kind: "digit",
          char: col.char,
          index,
          row: targetRow,
          translate: translateFor(targetRow),
          disableTransition: false,
          exiting: false,
          delayMs,
          durationMs,
        };
      }

      if (col.loops !== null) {
        const { delayMs, durationMs, visualLoops } = timing.perKey.get(col.key) ?? {
          delayMs: 0,
          durationMs: 0,
          visualLoops: col.loops ?? 0,
        };
        const targetDigit = Number(col.char);
        spinTargets.set(
          col.key,
          col.direction === 1
            ? this.baselineRow + visualLoops * 10 + targetDigit + (carrying ? 1 : 0)
            : this.baselineRow - visualLoops * 10 - ((10 - targetDigit) % 10),
        );
        this.rowByKey.set(col.key, this.baselineRow);
        return {
          key: col.key,
          kind: "digit",
          char: col.char,
          index,
          row: this.baselineRow,
          translate: translateFor(this.baselineRow),
          disableTransition: true,
          exiting: false,
          delayMs,
          durationMs,
        };
      }

      // Unchanged: already showing the right digit at its settled row — nothing to animate.
      const row = this.rowByKey.get(col.key) ?? this.baselineRow;
      return {
        key: col.key,
        kind: "digit",
        char: col.char,
        index,
        row,
        translate: translateFor(row),
        disableTransition: false,
        exiting: false,
        delayMs: 0,
        durationMs: 0,
      };
    });

    const columns = this.rebaseDriftedColumns(mappedColumns, priorRowByKey, spinTargets);

    // Sized off the rows actually targeted, not off baselineRow + loops: a diff that interrupts an
    // in-flight spin continues from rowByKey (which is not reset to baselineRow until settle), so
    // its targets can sit above what the loop count alone implies. rebaseDriftedColumns caps that
    // at MAX_ROWS_ABOVE_BASELINE; a rebased column's target lives in spinTargets, not its row yet.
    const requiredRows = Math.max(
      stripRowsFor(this.baselineRow, maxForwardLoops + (carryKeys.size ? 1 : 0)),
      ...columns.map((c) => c.row + 1),
      ...[...spinTargets.values()].map((row) => row + 1),
    );
    if (requiredRows > this.stripRows) {
      this.stripRows = requiredRows;
      this.digitStrip = buildDigitStrip(requiredRows);
    }

    this.renderColumns.set(columns);

    const exitingKeys = diff.filter((c) => c.exiting).map((c) => c.key);

    if (spinTargets.size) {
      // Moved a frame later so the move isn't part of this render. That alone doesn't guarantee the
      // snap to the start row has been applied (see resolveSpinTargets), which forces it.
      this.pendingSpinTargets = spinTargets;
      this.ngZone.runOutsideAngular(() => {
        this.enterRafId = requestAnimationFrame(() => {
          this.enterRafId = null;
          this.pendingSpinTargets = null;
          this.ngZone.run(() => {
            this.resolveSpinTargets(spinTargets);
            this.scheduleSettle(timing.totalMs + SETTLE_BUFFER_MS, exitingKeys);
          });
        });
      });
    } else {
      this.scheduleSettle(timing.totalMs + SETTLE_BUFFER_MS, exitingKeys);
    }
  }

  // Keeps a burst of interrupting diffs from ratcheting the strip up without limit. Each interrupt
  // continues a column from its in-flight row instead of from rest, so without this its target
  // climbs by a whole spin per interrupt and stripRows follows it up. A column targeted past
  // MAX_ROWS_ABOVE_BASELINE is shifted down by the whole loops its prior row had drifted above
  // baselineRow. The strip repeats every 10 rows, so the digits and the spin's remaining distance are
  // unchanged. It's rendered at the shifted start row with the transition off (a snap, cutting the
  // in-flight spin short), then moved to the shifted target a frame later via spinTargets, the same
  // snap-then-move dance a fresh spin uses (see resolveSpinTargets).
  // A column this diff leaves unchanged is skipped: snapping it would only cut its spin short.
  // Columns in spinTargets never get here: they sit at baselineRow, below the ceiling.
  private rebaseDriftedColumns(
    columns: RenderColumn[],
    priorRowByKey: Map<string, number>,
    spinTargets: Map<string, number>,
  ): RenderColumn[] {
    const ceiling = this.baselineRow + MAX_ROWS_ABOVE_BASELINE;
    return columns.map((c) => {
      if (c.kind !== "digit" || c.row < ceiling) {
        return c;
      }
      const priorRow = priorRowByKey.get(c.key) ?? this.baselineRow;
      if (c.row === priorRow) {
        return c;
      }
      const shift = Math.floor((priorRow - this.baselineRow) / 10) * 10;
      if (shift <= 0) {
        return c;
      }
      const startRow = priorRow - shift;
      spinTargets.set(c.key, c.row - shift);
      return { ...c, row: startRow, translate: translateFor(startRow), disableTransition: true };
    });
  }

  // Reduced-motion counterpart to settle() — same "row = baselineRow + digit, no transition" snap,
  // but computed straight from next's own digits instead of the previous animation's landing rows,
  // since reduced motion skips the animated diff (and its rowByKey bookkeeping) entirely.
  private snapToRest(next: string): void {
    const diff = diffOdometerColumns(this.previousFormatted, next, this.decimalSeparator);
    for (const col of diff) {
      if (col.exiting) {
        this.rowByKey.delete(col.key);
      }
    }

    let digitIndex = 0;
    this.renderColumns.set(
      diff
        .filter((col) => !col.exiting)
        .map((col) => {
          if (col.kind === "separator") {
            return {
              key: col.key,
              kind: "separator" as const,
              char: col.char,
              index: -1,
              row: 0,
              translate: "",
              disableTransition: false,
              exiting: false,
              delayMs: 0,
              durationMs: 0,
            };
          }
          const index = digitIndex++;
          const row = this.baselineRow + Number(col.char);
          this.rowByKey.set(col.key, row);
          return {
            key: col.key,
            kind: "digit" as const,
            char: col.char,
            index,
            row,
            translate: translateFor(row),
            disableTransition: true,
            exiting: false,
            delayMs: 0,
            durationMs: 0,
          };
        }),
    );
  }

  // Moves every column in `targetRows` from its start row (baseline "0" for a fresh spin, the
  // rebased start for a drifted one) to its real target (integer backward direct-slides instead,
  // so never reaches here). Called from applyDiff's rAF, or synchronously to resolve a pending one
  // early when a newer value pre-empts it.
  private resolveSpinTargets(targetRows: Map<string, number>): void {
    // Neither one rAF nor the same-task early resolve guarantees the start-row snap was applied; if
    // it merges with this move, a column jumps straight to the target (a forward spin can play
    // backward, a new column doesn't animate).
    this.forceStyleFlush();
    this.renderColumns.update((cols) =>
      cols.map((rc) => {
        const row = targetRows.get(rc.key);
        if (row === undefined) {
          return rc;
        }
        this.rowByKey.set(rc.key, row);
        return { ...rc, row, translate: translateFor(row), disableTransition: false };
      }),
    );
  }

  // Reading layout makes the browser apply any pending transition-off snap now, so a move written
  // after this transitions from the snapped row instead of merging with it.
  private forceStyleFlush(): void {
    this.host.nativeElement.getBoundingClientRect();
  }

  private pulseCrossFade(): void {
    if (this.crossFadeTimer) {
      clearTimeout(this.crossFadeTimer);
    }
    this.crossFadePulse.set(true);
    // Held for the full CSS transition (see .odometer__crossfade) rather than a single rAF frame —
    // flipping back a frame later would reverse the opacity transition before it's visibly dropped,
    // reading as a ~2% flicker instead of an actual fade.
    this.ngZone.runOutsideAngular(() => {
      this.crossFadeTimer = setTimeout(() => {
        this.crossFadeTimer = null;
        this.ngZone.run(() => this.crossFadePulse.set(false));
      }, CROSSFADE_DURATION_MS);
    });
  }

  private scheduleSettle(durationMs: number, exitingKeys: string[]): void {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
    }
    this.pendingCompletion = { exitingKeys };
    const duration = this.reducedMotion() ? CROSSFADE_DURATION_MS : durationMs;

    this.ngZone.runOutsideAngular(() => {
      this.settleTimer = setTimeout(() => {
        this.settleTimer = null;
        this.ngZone.run(() => this.finishSpin());
      }, duration);
    });
  }

  // Shared completion logic between scheduleSettle's timer firing normally and onWindowResize
  // forcing the same completion early.
  private finishSpin(): void {
    const { exitingKeys } = this.pendingCompletion;
    this.settle(exitingKeys);
    this.committed.emit();
  }

  // Closest-to-the-survivors-first stagger for a dropped column's width-collapse (see
  // EXIT_COLLAPSE_STAGGER_MS) — the place nearest the surviving digits collapses first (delay 0),
  // the next one out starts EXIT_COLLAPSE_STAGGER_MS later while the first is still moving, and so
  // on. exitingKeys is already ordered left-to-right (highest place first, see
  // backwardExitingIntColumns), i.e. the reverse of the order this stagger needs, so it's walked
  // back-to-front here.
  private staggerExitTimings(exitingKeys: string[]): Map<string, number> {
    const delayByKey = new Map<string, number>();
    for (let i = 0; i < exitingKeys.length; i++) {
      delayByKey.set(exitingKeys[exitingKeys.length - 1 - i], i * EXIT_COLLAPSE_STAGGER_MS);
    }
    return delayByKey;
  }

  private settle(exitingKeys: string[]): void {
    if (!this.renderColumns().length) {
      return;
    }

    const exitDelayByKey = this.staggerExitTimings(exitingKeys);

    // Re-normalize every remaining row to baselineRow + digit, transitions disabled, so the next
    // change always starts from a small, known row. delayMs/durationMs are zeroed too — the
    // template binds them directly, and leaving stale multi-second values armed would replay a
    // spurious spin if something later changes transform's resolved px (e.g. browser zoom) without
    // this component's knowledge. A dropped column (exitingKeys) is kept in place through this same
    // pass — it already spun down to 0 alongside everything else (see backwardExitingIntColumns) —
    // and only now gets `exiting: true`, starting its (staggered) width-collapse; it isn't actually
    // removed from rowByKey/the DOM until every collapse has had time to play (see the cleanup timer
    // below).
    this.renderColumns.update((cols) =>
      cols.map((c) => {
        if (c.kind !== "digit") {
          return c;
        }
        const digit = ((c.row % 10) + 10) % 10;
        const row = this.baselineRow + digit;
        this.rowByKey.set(c.key, row);
        return {
          ...c,
          row,
          translate: translateFor(row),
          disableTransition: true,
          delayMs: 0,
          durationMs: 0,
          exiting: exitingKeys.includes(c.key),
          exitDelayMs: exitDelayByKey.get(c.key) ?? 0,
          exitDurationMs: EXIT_COLLAPSE_DURATION_MS,
        };
      }),
    );

    // Every row is back within baselineRow + 9 now, with transitions off, so nothing can reference
    // a high index any more. Release whatever headroom the last spin (or a burst of interrupting
    // ones) grew the strip to, instead of keeping every mounted odometer at its high-water mark
    // for its whole lifetime.
    const restRows = stripRowsFor(this.baselineRow, 1);
    if (this.stripRows > restRows) {
      this.stripRows = restRows;
      this.digitStrip = buildDigitStrip(restRows);
    }

    // Two rAFs: the first only guarantees the DOM update flushed, not that the browser painted it —
    // re-enabling transitions before that paint would let the row drop above play as a visible
    // (backwards) transition.
    this.ngZone.runOutsideAngular(() => {
      this.settleRafId = requestAnimationFrame(() => {
        this.settleRafId = requestAnimationFrame(() => {
          this.settleRafId = null;
          this.ngZone.run(() => {
            this.renderColumns.update((cols) =>
              cols.map((c) => ({ ...c, disableTransition: false })),
            );
          });
        });
      });
    });

    if (exitingKeys.length) {
      this.ngZone.runOutsideAngular(() => {
        this.exitCleanupTimer = setTimeout(
          () => {
            this.exitCleanupTimer = null;
            this.ngZone.run(() => {
              for (const key of exitingKeys) {
                this.rowByKey.delete(key);
              }
              this.renderColumns.update((cols) => cols.filter((c) => !exitingKeys.includes(c.key)));
            });
          },
          (exitingKeys.length - 1) * EXIT_COLLAPSE_STAGGER_MS + EXIT_COLLAPSE_DURATION_MS,
        );
      });
    }
  }
}
